/**
 * CallRecordingPlayer
 *
 * Displays call recording access. Supports two URL types:
 * - LiveKit Cloud session URL → "Open in LiveKit" button (audio playback in LiveKit dashboard)
 * - Direct audio file URL → inline HTML audio player with play/pause and seek
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Play, Pause, Volume2, ExternalLink, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ICallRecordingPlayerProps } from '@/@types/voiceAgents';

function formatTime(seconds: number): string {
  const min = Math.floor(seconds / 60);
  const sec = Math.floor(seconds % 60);
  return `${min}:${String(sec).padStart(2, '0')}`;
}

function isLiveKitSessionUrl(url: string): boolean {
  return url.includes('cloud.livekit.io/projects/');
}

export function CallRecordingPlayer({ recordingUrl, className }: ICallRecordingPlayerProps) {
  if (!recordingUrl) {
    return (
      <div
        className={cn(
          'flex items-center justify-center py-16 text-muted-foreground text-sm',
          className
        )}
      >
        No recording available for this call
      </div>
    );
  }

  if (isLiveKitSessionUrl(recordingUrl)) {
    return <LiveKitSessionLink recordingUrl={recordingUrl} className={className} />;
  }

  return <DirectAudioPlayer recordingUrl={recordingUrl} className={className} />;
}

/** Opens LiveKit Cloud Agent Insights page for audio playback and download */
function LiveKitSessionLink({
  recordingUrl,
  className,
}: {
  recordingUrl: string;
  className?: string;
}) {
  return (
    <div className={cn('p-6 space-y-4', className)}>
      <div className="h-24 flex items-center justify-center bg-muted rounded-lg">
        <div className="flex items-center gap-0.5 h-12">
          {Array.from({ length: 32 }).map((_, i) => (
            <div
              key={i}
              className="w-1 rounded-full bg-muted-foreground/40"
              style={{ height: `${20 + Math.sin(i * 0.5) * 15}px` }}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Recording available in LiveKit Cloud (30-day retention)
        </p>
        <Button variant="outline" asChild>
          <a href={recordingUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-4 h-4 mr-2" />
            Open in LiveKit
          </a>
        </Button>
      </div>
    </div>
  );
}

/** Inline audio player for direct audio file URLs (S3, R2, etc.) */
function DirectAudioPlayer({
  recordingUrl,
  className,
}: {
  recordingUrl: string;
  className?: string;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animFrameRef = useRef<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  // Stable rAF tick function: captures audioRef/animFrameRef/setProgress via closure.
  // Stored in a ref (not useCallback) so it can self-reference without TDZ issues.
  const updateProgress = useCallback(() => {
    const tick = () => {
      if (audioRef.current) {
        setProgress(audioRef.current.currentTime);
        if (!audioRef.current.paused) {
          animFrameRef.current = requestAnimationFrame(tick);
        }
      }
    };
    tick();
  }, []);

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(recordingUrl);
      audioRef.current.addEventListener('loadedmetadata', () => {
        if (audioRef.current) setDuration(audioRef.current.duration);
      });
      audioRef.current.addEventListener('ended', () => {
        setIsPlaying(false);
        setProgress(0);
        cancelAnimationFrame(animFrameRef.current);
      });
    }

    if (isPlaying) {
      audioRef.current.pause();
      cancelAnimationFrame(animFrameRef.current);
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(() => setIsPlaying(false));
      setIsPlaying(true);
      animFrameRef.current = requestAnimationFrame(updateProgress);
    }
  }, [recordingUrl, isPlaying, updateProgress]);

  const handleSeek = useCallback((value: number[]) => {
    const newTime = value[0] ?? 0;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
    setProgress(newTime);
  }, []);

  return (
    <div className={cn('p-6 space-y-4', className)}>
      <div className="h-24 flex items-center justify-center bg-muted rounded-lg">
        <div className="flex items-center gap-0.5 h-12">
          {Array.from({ length: 32 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'w-1 rounded-full transition-colors',
                isPlaying ? 'bg-primary' : 'bg-muted-foreground/40'
              )}
              style={{ height: `${20 + Math.sin(i * 0.5) * 15}px` }}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={handlePlayPause}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        </Button>

        <div className="flex-1">
          <Slider
            value={[progress]}
            max={duration || 1}
            step={0.1}
            onValueChange={handleSeek}
            className="w-full"
          />
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-20 justify-end">
          <Volume2 className="w-4 h-4" />
          <span>
            {formatTime(progress)} / {formatTime(duration)}
          </span>
        </div>

        <Button variant="ghost" size="icon" asChild title="Download recording">
          <a href={recordingUrl} download>
            <Download className="w-4 h-4" />
          </a>
        </Button>
      </div>
    </div>
  );
}
